import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { createCarrier, resetCreateStatus, resetUpdateStatus, selectCreateCarrierError, selectCreateCarrierLoading, selectSelectedCarrier, selectSelectedCarrierLoading, selectUpdateCarrierError, selectUpdateCarrierLoading, updateCarrier } from "../../redux/carriers/carriersSlice";
import { closeModal, setHasToRefresh } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";

export default function AddEditCarrier({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const selectedCarrier = useSelector(selectSelectedCarrier);
    const selectedCarrierLoading = useSelector(selectSelectedCarrierLoading);
    const createLoading = useSelector(selectCreateCarrierLoading);
    const createError = useSelector(selectCreateCarrierError);
    const updateLoading = useSelector(selectUpdateCarrierLoading);
    const updateError = useSelector(selectUpdateCarrierError);
    
    // Form state
    const [formData, setFormData] = useState({
        company: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        state: 'IT',
        phone: '',
        mobile: '',
        email: '',
        vatNumber: '',
        fiscalcode: '',
        active: true,
        prospect: false
    });
    
    const [validation, setValidation] = useState({
        company: true
    });
    
    // Fetch carrier details if in edit mode
    useEffect(() => {
        if (mode === 'edit' && selectedCarrier?._id) {
            setFormData({
                company: selectedCarrier.company || '',
                address: selectedCarrier.address || '',
                city: selectedCarrier.city || '',
                province: selectedCarrier.province || '',
                postalCode: selectedCarrier.postalCode || '',
                state: selectedCarrier.state || 'IT',
                phone: selectedCarrier.phone || '',
                mobile: selectedCarrier.mobile || '',
                email: selectedCarrier.email || '',
                vatNumber: selectedCarrier.vatNumber || '',
                fiscalcode: selectedCarrier.fiscalcode || '',
                active: selectedCarrier.active !== false,
                prospect: selectedCarrier.prospect === true
            });
        }
    }, [mode, selectedCarrier]);
    
    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Reset validation for this field
        if (name in validation) {
            setValidation(prev => ({
                ...prev,
                [name]: true
            }));
        }
    };
    
    // Validate form
    const validateForm = () => {
        const newValidation = {
            company: !!formData.company.trim()
        };
        
        setValidation(newValidation);
        return Object.values(newValidation).every(isValid => isValid);
    };
    
    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        const carrierData = {
            ...formData,
            accountId: accountData._id
        };
        
        if (mode === 'add') {
            dispatch(createCarrier({
                carrier: carrierData,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                }
            });
        } else {
            dispatch(updateCarrier({
                carrier: {
                    ...carrierData,
                    _id: selectedCarrier._id
                },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                }
            });
        }
    };
    
    // Reset error states when component unmounts
    useEffect(() => {
        return () => {
            dispatch(resetCreateStatus());
            dispatch(resetUpdateStatus());
        };
    }, [dispatch]);
    
    if (mode === 'edit' && selectedCarrierLoading) {
        return <LoadingSection />;
    }
    
    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">
                {mode === 'add' ? 'Aggiungi Nuovo Corriere' : 'Modifica Corriere'}
            </h2>
            
            {(createLoading || updateLoading) && <LoadingSection isFixed />}
            
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Ragione Sociale */}
                    <div className="col-span-2">
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                            Ragione Sociale*
                        </label>
                        <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className={`w-full p-2 border ${!validation.company ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            placeholder="Ragione Sociale"
                        />
                        {!validation.company && (
                            <p className="text-red-500 text-sm mt-1">La ragione sociale è obbligatoria</p>
                        )}
                    </div>
                    
                    {/* Partita IVA */}
                    <div>
                        <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">
                            Partita IVA
                        </label>
                        <input
                            type="text"
                            id="vatNumber"
                            name="vatNumber"
                            value={formData.vatNumber}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Partita IVA"
                        />
                    </div>
                    
                    {/* Codice Fiscale */}
                    <div>
                        <label htmlFor="fiscalcode" className="block text-sm font-medium text-gray-700 mb-1">
                            Codice Fiscale
                        </label>
                        <input
                            type="text"
                            id="fiscalcode"
                            name="fiscalcode"
                            value={formData.fiscalcode}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Codice Fiscale"
                        />
                    </div>
                    
                    {/* Indirizzo */}
                    <div className="col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                            Indirizzo
                        </label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Indirizzo"
                        />
                    </div>
                    
                    {/* Città */}
                    <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                            Città
                        </label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Città"
                        />
                    </div>
                    
                    {/* Provincia */}
                    <div>
                        <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">
                            Provincia
                        </label>
                        <input
                            type="text"
                            id="province"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Provincia"
                            maxLength="2"
                        />
                    </div>
                    
                    {/* Codice Postale */}
                    <div>
                        <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                            CAP
                        </label>
                        <input
                            type="text"
                            id="postalCode"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="CAP"
                        />
                    </div>
                    
                    {/* Nazione */}
                    <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                            Nazione
                        </label>
                        <input
                            type="text"
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nazione (es. IT)"
                            maxLength="2"
                        />
                    </div>
                    
                    {/* Email */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Email"
                        />
                    </div>
                    
                    {/* Telefono */}
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Telefono
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Telefono"
                        />
                    </div>
                    
                    {/* Cellulare */}
                    <div>
                        <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                            Cellulare
                        </label>
                        <input
                            type="tel"
                            id="mobile"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Cellulare"
                        />
                    </div>
                    
                    {/* Checkbox Attivo */}
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="active"
                            name="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="active" className="text-sm font-medium text-gray-700">
                            Attivo
                        </label>
                    </div>
                    
                    {/* Checkbox Prospect */}
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="prospect"
                            name="prospect"
                            checked={formData.prospect}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="prospect" className="text-sm font-medium text-gray-700">
                            Prospect
                        </label>
                    </div>
                </div>
                
                {/* Error message */}
                {(createError || updateError) && (
                    <div className="p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{createError || updateError}</p>
                    </div>
                )}
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>
                    
                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                {mode === 'add' ? 'Crea Corriere' : 'Aggiorna Corriere'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
